import type { ProcessedMetadata } from './metadata/main-metadata';
import { getFieldDependentTable } from './metadata/tables';
import { queryClient } from './constants';
import { prefetchQueryIfAbsent } from './prefetchIfAbsent';
import { queryPolicies } from './queryPolicies';
import { merchantRepsQueryOptions } from './useMerchantReps';
import { recordDriveFolderQueryOptions } from '../features/docs/useDriveFiles';
import {
  activePrimaryOverrideQueryOptions,
  merchantDealCyclesQueryOptions,
  merchantDetailRequest,
  merchantSubmissionsRequest,
} from './destinationQueries/merchantPanel';

export interface RecordDestinationPrefetchTarget {
  tableName: string;
  id: string | number;
}

interface DestinationPrefetchContext {
  id: number;
  tableName: string;
  metadata: ProcessedMetadata;
}

type DestinationPrefetchLoader = (context: DestinationPrefetchContext) => Promise<void>;

function validRecordId(value: string | number): boolean {
  return /^\d+$/.test(String(value)) && Number(value) > 0;
}

async function prefetchStructuralRequest(
  request: Parameters<typeof getFieldDependentTable>[0],
  metadata: ProcessedMetadata,
): Promise<void> {
  const { error } = await getFieldDependentTable({
    ...request,
    onlyIfAbsent: true,
    staleTime: queryPolicies.recordDetail.staleTime,
    gcTime: queryPolicies.recordDetail.gcTime,
    tableMetadata: metadata.tableData ?? undefined,
  });
  if (error) throw error;
}

/**
 * The merchant side panel's complete initial data boundary.
 *
 * Every task corresponds to a query mounted immediately by MerchantPanelBody
 * or its default visible children. Tasks run in parallel and must never invoke
 * another destination loader; that makes one hovered destination the hard
 * recursion boundary.
 */
const prefetchMerchantPanel: DestinationPrefetchLoader = async ({ id, metadata }) => {
  const merchantSchema = metadata.tableData?.byName?.merchant?.schema_name || 'public';
  const submissionSchema = metadata.tableData?.byName?.submission?.schema_name || 'public';

  await Promise.all([
    prefetchStructuralRequest(merchantDetailRequest(id, merchantSchema), metadata),
    prefetchStructuralRequest(merchantSubmissionsRequest(id, submissionSchema), metadata),
    prefetchQueryIfAbsent(queryClient, merchantRepsQueryOptions(id)),
    prefetchQueryIfAbsent(queryClient, activePrimaryOverrideQueryOptions(id)),
    prefetchQueryIfAbsent(queryClient, recordDriveFolderQueryOptions('merchant', id)),
    prefetchQueryIfAbsent(queryClient, merchantDealCyclesQueryOptions(id)),
  ]);
};

const destinationLoaders: Readonly<Record<string, DestinationPrefetchLoader>> = {
  merchant: prefetchMerchantPanel,
};

async function prefetchGenericRecordDetail({
  id,
  tableName,
  metadata,
}: DestinationPrefetchContext): Promise<void> {
  const schema = metadata.tableData?.byName?.[tableName]?.schema_name;
  if (!schema) return;

  await prefetchStructuralRequest({
    tableName,
    schema,
    deep: true,
    conditions: {
      id: {
        filterType: 'number',
        type: 'equals',
        filter: id,
        colId: 'id',
      },
    },
    limitOffset: { limit: 1, offset: 0 },
    withCount: false,
    singleRow: true,
  }, metadata);
}

/** Resolve the hovered record's destination at runtime and warm its bounded loader. */
export async function prefetchRecordDestinationIfAbsent(
  target: RecordDestinationPrefetchTarget,
  metadata: ProcessedMetadata | null | undefined,
): Promise<void> {
  if (!metadata || !validRecordId(target.id)) return;

  const context: DestinationPrefetchContext = {
    id: Number(target.id),
    tableName: target.tableName,
    metadata,
  };
  const loader = destinationLoaders[target.tableName] ?? prefetchGenericRecordDetail;
  await loader(context);
}
